<template>
  <WdsAlert v-if="errorBannerMessage" error class="m-b-15">{{ errorBannerMessage }}</WdsAlert>
  <WdsStack direction-column gap-custom="2rem">
    <WdsText v-if="notes.length === 0" class="tw-text-center" data-test="recruiter-no-notes">
      {{ $t('No notes for this contact') }}
    </WdsText>
    <div
      v-for="note in notes"
      :key="note.id"
      class="tw-bg-true-gray-100 tw-rounded-sm tw-px-3 tw-py-3"
    >
      <WdsStack direction-row gap-sm>
        <WdsAvatar xs :initials="note.author.initials ?? ''" :path="note.author.avatarPath ?? ''" />

        <WdsStack direction-column gap-sm class="tw-grow">
          <WdsStack direction-row justify-space-between gap-sm>
            <WdsStack direction-column gap-custom="0" class="tw-grow">
              <WdsText :text="note.author.name" />

              <WdsStack direction-row gap-xs>
                <WdsText tertiary>
                  {{ note.createdAt }}
                  {{ note.hasBeenUpdated ? `(${$t('edited')})` : '' }}
                </WdsText>
              </WdsStack>
            </WdsStack>

            <WdsMenu placement="bottom-end">
              <template #trigger>
                <WdsButtonGhost>
                  <WdsIcon name="overflow-menu-horizontal" :size="16" />
                </WdsButtonGhost>
              </template>

              <WdsMenuOption
                :disabled="loading || isMutating || !note.viewerCanUpdate"
                @click="showEditForm(note)"
              >
                <WdsIcon :size="16" name="edit" />
                {{ $t('Edit note') }}
              </WdsMenuOption>

              <WdsMenuOption
                theme="DESTRUCTIVE"
                :disabled="loading || isMutating || !note.viewerCanDelete"
                @click="note.viewerCanDelete ? runDeleteNoteMutation(note.id) : undefined"
              >
                <WdsIcon :size="16" name="trash-can" />
                {{ $t('Delete') }}
              </WdsMenuOption>
            </WdsMenu>
          </WdsStack>

          <WdsText class="tw-break-all" secondary>{{ note.body }}</WdsText>
        </WdsStack>
      </WdsStack>
    </div>
    <WdsButton
      v-if="!formIsVisible"
      full-width
      secondary
      data-test="recruiter-add-note"
      @click="showAddForm"
    >
      {{ $t('Add note') }}
    </WdsButton>

    <div v-if="formIsVisible">
      <WdsInputArea
        v-model="selectedNoteBody"
        name="note-body"
        :disabled="loading || isMutating"
        :error="errors['body']"
        :placeholder="$t('Write notes about the recruiter for internal use...')"
        data-test="recruiter-note-input"
      />
      <WdsStack align-center justify-end direction-row gap-sm class="tw-mt-4">
        <WdsSpinner v-if="loading || isMutating" data-test="recruiter-note-input-loading" indeterminate />

        <WdsButton tertiary :disabled="loading || isMutating" @click="closeForm">{{ $t('Cancel') }}</WdsButton>
        <WdsButton
          v-if="selectedNoteId === null"
          primary
          :disabled="loading || isMutating || !selectedNoteBody"
          data-test="recruiter-save-note"
          @click="saveNote"
        >
          {{ $t('Add note') }}
        </WdsButton>
        <WdsButton v-if="selectedNoteId !== null" primary :disabled="loading || isMutating" @click="saveNote">
          {{ $t('Save note') }}
        </WdsButton>
      </WdsStack>
    </div>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick, Ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsStack,
  WdsAlert,
  WdsAvatar,
  WdsMenu,
  WdsMenuOption,
  WdsIcon,
  WdsText,
  WdsButton,
  WdsButtonGhost,
  WdsInputArea,
  WdsSpinner,
} from '@worksome/wds-components'

import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { deepClone } from '@/utils/tools'
import { FormValidation } from '@/utils/formValidation'
import { COMPANY_RECRUITER_NOTES_FRAGMENT } from '@/components/RecruiterDetailPanel/graphql/fragments'
import { CompanyRecruiterNotesQuery } from '@/components/RecruiterDetailPanel/graphql/queries'
import { CreateNoteMutation } from '@/components/GraphQL/mutations/Notes/CreateNoteMutation'
import { UpdateNoteMutation } from '@/components/GraphQL/mutations/Notes/UpdateNoteMutation'
import { DeleteNoteMutation } from '@/components/GraphQL/mutations/Notes/DeleteNoteMutation'
import { useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { NOTE_VIEW_FRAGMENT } from '@/components/GraphQL/queries/Notes'
import { useLocaleTimeAgo } from '@/composables/useLocaleTimeAgo'

const props = defineProps<{
  companyRecruiterId: string
  accountId: string
}>()

const emit = defineEmits(['update-notes'])

const errorBannerMessage = ref(null)
const errors = ref({})

const formIsVisible: Ref<boolean> = ref(false)
const selectedNoteId: Ref<string | null> = ref(null)
const selectedNoteBody: Ref<string> = ref('')

const variables = computed(() => ({
  companyRecruiterId: props.companyRecruiterId,
}))

const { result, loading } = useQuery(CompanyRecruiterNotesQuery, variables, {
  clientId: 'publicApi',
})

const isMutating = ref(false)

const notes = computed(() => {
  const crNotes = useFragment(COMPANY_RECRUITER_NOTES_FRAGMENT, result.value?.companyRecruiter)

  return (crNotes?.notes.data ?? [])
    .map((note) => useFragment(NOTE_VIEW_FRAGMENT, note))
    .map((note) => ({
      ...note,
      createdAt: useLocaleTimeAgo(note.createdAt).value,
      hasBeenUpdated: note.createdAt !== note.updatedAt,
    }))
})

const resetErrors = () => {
  errors.value = {}
  errorBannerMessage.value = null
}

const closeForm = () => {
  formIsVisible.value = false
  selectedNoteId.value = null
  selectedNoteBody.value = ''
}

const showAddForm = () => {
  formIsVisible.value = true
}

const showEditForm = (note) => {
  formIsVisible.value = false

  nextTick(() => {
    selectedNoteId.value = note.id
    selectedNoteBody.value = note.body
    formIsVisible.value = true
  })
}

const runCreateNoteMutation = () => {
  isMutating.value = true

  publicApi
    .mutate({
      mutation: CreateNoteMutation,
      variables: {
        input: {
          body: selectedNoteBody.value,
          accountId: props.accountId,
          notableId: props.companyRecruiterId,
        },
      },
      update(cache, { data }) {
        if (data) {
          const { createNote } = data

          const newData = deepClone(
            cache.readQuery({
              query: CompanyRecruiterNotesQuery,
              variables: {
                companyRecruiterId: props.companyRecruiterId,
              },
            }),
          )

          newData.companyRecruiter.notes.data.push(createNote)
          newData.companyRecruiter.notes.paginatorInfo.total += 1

          cache.writeQuery({
            query: CompanyRecruiterNotesQuery,
            variables: {
              companyRecruiterId: props.companyRecruiterId,
            },
            data: newData,
          })
        }
      },
    })
    .then(() => {
      resetErrors()

      closeForm()
    })
    .catch((createNoteMutationError) => {
      resetErrors()
      const { graphQLErrors } = createNoteMutationError

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        errors.value = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
      } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }
    })
    .finally(() => {
      isMutating.value = false
      emit('update-notes')
    })
}

const runEditNoteMutation = () => {
  if (selectedNoteId.value === null) return
  isMutating.value = true

  publicApi
    .mutate({
      mutation: UpdateNoteMutation,
      variables: {
        input: {
          id: selectedNoteId.value,
          body: selectedNoteBody.value,
        },
      },
    })
    .then(() => {
      resetErrors()

      closeForm()
    })
    .catch((updateNoteMutationError) => {
      resetErrors()

      const { graphQLErrors } = updateNoteMutationError

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        errors.value = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
      } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }
    })
    .finally(() => {
      isMutating.value = false
      emit('update-notes')
    })
}

const saveNote = () => {
  if (selectedNoteId.value === null) {
    runCreateNoteMutation()
  } else {
    runEditNoteMutation()
  }
}

function runDeleteNoteMutation(noteId: string) {
  closeForm()

  isMutating.value = true

  publicApi
    .mutate({
      mutation: DeleteNoteMutation,
      variables: {
        input: {
          id: noteId,
        },
      },
      update(cache, { data }) {
        if (data) {
          const { deleteNote } = data
          const newData = deepClone(
            cache.readQuery({
              query: CompanyRecruiterNotesQuery,
              variables: {
                companyRecruiterId: props.companyRecruiterId,
              },
            }),
          )

          const index = newData.companyRecruiter.notes.data.findIndex((n) => n.id === deleteNote.id)

          if (index !== -1) {
            newData.companyRecruiter.notes.data.splice(index, 1)
            newData.companyRecruiter.notes.paginatorInfo.total -= 1
            cache.writeQuery({
              query: CompanyRecruiterNotesQuery,
              variables: {
                companyRecruiterId: props.companyRecruiterId,
              },
              data: newData,
            })
          }
        }
      },
    })
    .then(() => {
      resetErrors()
    })
    .catch((deleteNoteMutationError) => {
      resetErrors()

      const { graphQLErrors } = deleteNoteMutationError

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        errors.value = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
      } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }
    })
    .finally(() => {
      isMutating.value = false
      emit('update-notes')
    })
}
</script>
